import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiSearchLandingPage } from '#/modules/cms/api/types/CmsApiSearchLandingPage';
import { SearchBody } from '#/modules/search/components/Body/SearchBody';
import { SearchResultsHeader } from '#/modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from '#/modules/search/components/Results/Hits/SearchResultsHits';
import { useSearchFacetsByType } from '#/modules/search/constants/useSearchFacetsByType';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchLocation,
  useSearchSort,
} from '#/modules/search/contexts/SearchContext';
import { useSaveSearch } from '#/modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import { useSearchPageLifecycle } from '#/modules/search/pages/Search/useSearchPageLifecycle';
import {
  useSearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';

import { SearchDescriptionBody } from './SearchDescriptionBody';

type Props = {
  content: CmsApiSearchLandingPage;
};

export function SearchLandingPage({ content }: Props) {
  const searchType = content.filters.type;
  const { i18n } = useI18n();

  // Get search facets for this search type
  const searchFacetsByType = useSearchFacetsByType(i18n);
  const searchFacets = searchFacetsByType[searchType];

  const isSearching = useIsSearching();
  const searchLocation = useSearchLocation();
  const sort = useSearchSort();

  const { saveSearch } = useSaveSearch();
  const { setSearchUrlPageIndex, setSearchUrlSort } = useSearchPageActions({
    isInSearchLandingPage: true,
  });

  const { getSearchTrackingData } = useSearchAnalytics();

  const { initialized } = useSearchPageLifecycle({
    searchType,
    getSearchTrackingData,
    searchFacets,
    isSearchLandingPage: true,
  });

  const currentPageResults = useSearchCurrentPageResults();
  const results = initialized ? currentPageResults : undefined;

  const { searchSavedListingsStore } = useSearchSavedListingsStore();
  useSearchSavedListingsSyncAlgoliaHits({
    hits: results?.hits,
    searchSavedListingsStore,
  });

  return (
    <SearchBody>
      <SearchDescriptionBody content={content} />

      <SearchResultsHeader
        results={results}
        isSearching={isSearching}
        showSortOptions={false}
        sort={sort}
        searchType={searchType}
        saveSearch={saveSearch}
        onChangeSort={setSearchUrlSort}
      />

      <SearchResultsHits
        results={results}
        isSearching={isSearching}
        searchType={searchType}
        searchLocation={searchLocation}
        saveSearch={saveSearch}
        getSearchTrackingData={getSearchTrackingData}
        onChangePageIndex={setSearchUrlPageIndex}
        searchSavedListingsStore={searchSavedListingsStore}
      />
    </SearchBody>
  );
}
